import { useTranslation } from 'react-i18next';
import type { AlbumFont, AlbumStyles, AlbumTextAlign, ItineraryDay, JournalEntry } from '../../types';
import { boxFontChoices } from '../../utils/albumLook';
import AlbumFontSelect from './AlbumFontSelect';
import { AlignCenter, AlignLeft, AlignRight, Icon } from '../ui/Icon';
import AlbumTextSizeControl from './AlbumTextSizeControl';

interface Props {
  kind: 'TITLE' | 'TEXT';
  text: string;
  maxChars: number;
  entries: JournalEntry[];
  days: ItineraryDay[];
  saving: boolean;
  onSave: (text: string | null) => void;
  /** ADR-0053: the box's size in pt, and the range its kind can take. */
  pt: number;
  range: { min: number; max: number };
  align: AlbumTextAlign;
  /** ADR-0053: the box's own font id, or null for the album pair's (`pairFont`). */
  font: string | null;
  styles: AlbumStyles;
  pairFont: AlbumFont;
  onStyle: (style: { textPt?: number; textAlign?: AlbumTextAlign; font?: string | null }) => void;
}

const ALIGNS = [{ id: 'left', icon: AlignLeft }, { id: 'center', icon: AlignCenter }, { id: 'right', icon: AlignRight }] as const;
const segment = (on: boolean) =>
  `px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${on ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500'}`;

/**
 * What the page can't do inline: pull an entry from the diary, or empty the
 * box. ADR-0046: the entry is pasted as a copy — from then on the words are the album's.
 */
export default function AlbumTextTools({ kind, text, maxChars, entries, days, saving, onSave, pt, range, align, font, styles, pairFont, onStyle }: Props) {
  const { t } = useTranslation();
  const dayNumber = new Map(days.map(d => [d.id, d.dayNumber]));

  function insert(entryId: string) {
    const entry = entries.find(e => e.id === entryId);
    if (!entry) return;
    onSave((text.trim() ? `${text.trimEnd()}\n\n${entry.body}` : entry.body).slice(0, maxChars));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <AlbumTextSizeControl key={pt} pt={pt} range={range} disabled={saving} onChange={textPt => onStyle({ textPt })} />
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5" role="radiogroup" aria-label={t('album.style.textAlign')}>
          {ALIGNS.map(a => (
            <button key={a.id} role="radio" aria-checked={align === a.id} disabled={saving} onClick={() => onStyle({ textAlign: a.id })}
              className={segment(align === a.id)} aria-label={t(`album.style.align_${a.id}`)}>
              <Icon as={a.icon} size={13} />
            </button>
          ))}
        </div>
      </div>
      <AlbumFontSelect
        label={t('album.style.font')}
        choices={[
          { value: null, label: t('album.style.fontOfAlbum'), font: pairFont },
          ...boxFontChoices(styles, kind, font).map(c => ({
            value: c.id,
            label: c.pairs.length ? c.pairs.map(id => t(`album.fontPairs.${id}`, { defaultValue: id })).join(' · ') : c.font.family,
            font: c.font,
          })),
        ]}
        value={font}
        disabled={saving}
        onChange={next => onStyle({ font: next })}
      />
      <div className="flex items-center gap-2">
        {kind === 'TEXT' && entries.length > 0 && (
          <select
            value=""
            onChange={e => insert(e.target.value)}
            disabled={saving}
            aria-label={t('album.fromDiary')}
            className="min-w-0 flex-1 truncate text-xs border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 cursor-pointer"
          >
            <option value="">{t('album.fromDiary')}</option>
            {entries.map(e => (
              <option key={e.id} value={e.id}>
                {t('album.entryOption', { number: dayNumber.get(e.dayId) ?? '?', text: e.body.slice(0, 40) })}
              </option>
            ))}
          </select>
        )}
        {text && (
          <button onClick={() => onSave(null)} disabled={saving} className="btn-secondary text-xs">
            {t('common.clear')}
          </button>
        )}
      </div>
    </div>
  );
}

